import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight, Phone, Mail, MapPin, Clock, ChevronDown,
  Zap, BadgeCheck, Quote, BookOpen, ChefHat, Briefcase, Instagram, Facebook,
} from "lucide-react";
import { SafeImg, SectionHead, Stars } from "./StoreKit";
import EditorCanvas from "../editor/EditorCanvas";

const TYPE_ICONS = { services: Zap, menu: ChefHat, courses: BookOpen, posts: BookOpen, projects: Briefcase };
const MOTION_DUR = { smooth: 0.6, snappy: 0.3, slow: 1.1 };

const R = ({ children, delay = 0, dur = 0.6, className = "" }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }} transition={{ duration: dur, delay, ease: "easeOut" }}>
    {children}
  </motion.div>
);

function Counter({ value, dur = 1200 }) {
  const num = parseInt(String(value).replace(/[^0-9]/g, ""), 10) || 0;
  const suffix = String(value).replace(/[0-9,]/g, "");
  const [n, setN] = useState(0);
  useEffect(() => {
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      setN(Math.round(num * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [num, dur]);
  return <span>{n.toLocaleString("en-IN")}{suffix}</span>;
}

function Faq({ items, cardCls, cardExtra }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="space-y-3">
      {items.map((f, i) => (
        <div key={i} className={`${cardCls} overflow-hidden`} style={cardExtra} data-testid={`faq-${i}`}>
          <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between px-6 py-4 text-left">
            <span className="font-bold text-sm" style={{ color: "var(--sp)" }}>{f.q}</span>
            <ChevronDown className={`h-4 w-4 shrink-0 transition-transform duration-300 ${open === i ? "rotate-180" : ""}`} style={{ color: "var(--sa)" }} />
          </button>
          <motion.div initial={false} animate={{ height: open === i ? "auto" : 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
            <p className="px-6 pb-4 text-sm" style={{ color: "var(--st)", opacity: 0.7 }}>{f.a}</p>
          </motion.div>
        </div>
      ))}
    </div>
  );
}

/* Per-industry footer — the biggest "different agency" tell. */
function SiteFooter({ theme, site, store, head, contact, brand = "" }) {
  const cat = theme?.website_types?.[0] || "other";
  const name = store?.name || theme?.name || "";
  const year = new Date().getFullYear();
  const tagline = site?.footer?.tagline || "";
  const explore = site?.footer?.explore || [];
  const company = site?.footer?.company || [];

  const socials = (
    <div className="flex gap-2">
      {[Instagram, Facebook].map((Icon, i) => (
        <span key={i} className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors cursor-pointer">
          <Icon className="h-4 w-4" />
        </span>
      ))}
    </div>
  );

  if (cat === "portfolio") {
    return (
      <footer className="border-t px-6 py-16 text-center" style={{ borderColor: "color-mix(in srgb, var(--st) 12%, transparent)" }} data-testid="site-footer">
        <p className={`${head} text-3xl sm:text-4xl tracking-tighter`} style={{ color: "var(--sp)" }}>{name}</p>
        <p className="text-sm mt-3 max-w-md mx-auto" style={{ color: "var(--st)", opacity: 0.6 }}>{tagline}</p>
        <div className="flex justify-center gap-3 mt-7">
          {[Instagram, Facebook, Mail].map((Icon, i) => (
            <span key={i} className="h-10 w-10 rounded-full flex items-center justify-center transition-transform hover:scale-110 cursor-pointer"
              style={{ backgroundColor: "var(--ss)", color: "var(--sp)" }}>
              <Icon className="h-4 w-4" />
            </span>
          ))}
        </div>
        <p className="text-xs mt-10" style={{ color: "var(--st)", opacity: 0.4 }}>© {year} {name} · {brand ? `Powered by ${brand}` : "Powered by MILTROO"}</p>
      </footer>
    );
  }

  if (cat === "agency") {
    return (
      <footer style={{ backgroundColor: "var(--sp)" }} className="text-white" data-testid="site-footer">
        <div className="max-w-5xl mx-auto px-6 py-20 text-center">
          <p className="uppercase text-[11px] tracking-[0.35em] font-extrabold" style={{ color: "var(--sa)" }}>Ready when you are</p>
          <p className={`${head} text-4xl sm:text-6xl tracking-tighter mt-5 leading-[1.02]`}>Let's build something unmissable.</p>
          <a href={`mailto:${contact.email}`} data-testid="footer-cta"
            className="inline-flex items-center gap-2 mt-9 px-9 py-4 rounded-full font-bold text-sm text-white active:scale-95 transition-transform"
            style={{ backgroundColor: "var(--sa)" }}>
            {contact.email} <ArrowRight className="h-4 w-4" />
          </a>
        </div>
        <div className="border-t border-white/10 py-5 text-center text-xs text-white/40">© {year} {name} · {brand ? `Powered by ${brand}` : "Powered by MILTROO"}</div>
      </footer>
    );
  }

  if (cat === "restaurant" || cat === "local") {
    return (
      <footer style={{ backgroundColor: "var(--sp)" }} className="text-white" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid sm:grid-cols-3 gap-10">
          <div>
            <p className={`${head} text-xl`}>{name}</p>
            <p className="text-sm text-white/60 mt-3 leading-relaxed">{tagline}</p>
            <div className="mt-4">{socials}</div>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-widest font-extrabold text-white/50 mb-4">Visit Us</p>
            <div className="space-y-2.5 text-sm text-white/75">
              <p className="flex items-start gap-2"><MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" />{contact.address}</p>
              {contact.hours && <p className="flex items-center gap-2"><Clock className="h-3.5 w-3.5" />{contact.hours}</p>}
              <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" />{contact.phone}</p>
            </div>
          </div>
          <div className="sm:text-right">
            <p className="text-[11px] uppercase tracking-widest font-extrabold text-white/50 mb-4">{cat === "restaurant" ? "Reservations" : "Get In Touch"}</p>
            <a href={`tel:${contact.phone}`} data-testid="footer-cta"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-bold text-sm text-white active:scale-95 transition-transform"
              style={{ backgroundColor: "var(--sa)" }}>
              {cat === "restaurant" ? "Reserve a Table" : "Call Now"} <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
        <div className="border-t border-white/10 py-5 text-center text-xs text-white/40">© {year} {name} · {brand ? `Powered by ${brand}` : "Powered by MILTROO"}</div>
      </footer>
    );
  }

  return (
    <footer style={{ backgroundColor: "var(--sp)" }} className="text-white" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <p className={`${head} text-xl`}>{name}</p>
          <p className="text-sm text-white/60 mt-3 leading-relaxed">{tagline}</p>
          <div className="mt-4">{socials}</div>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-widest font-extrabold text-white/50 mb-4">Explore</p>
          <div className="space-y-2.5">{explore.map((e) => <p key={e} className="text-sm text-white/75">{e}</p>)}</div>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-widest font-extrabold text-white/50 mb-4">Company</p>
          <div className="space-y-2.5">{company.map((c) => <p key={c} className="text-sm text-white/75">{c}</p>)}</div>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-widest font-extrabold text-white/50 mb-4">Contact</p>
          <div className="space-y-2.5 text-sm text-white/75">
            {contact.phone && <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" />{contact.phone}</p>}
            {contact.email && <p>{contact.email}</p>}
            {contact.address && <p className="flex items-start gap-2"><MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" />{contact.address}</p>}
            {contact.hours && <p className="flex items-center gap-2"><Clock className="h-3.5 w-3.5" />{contact.hours}</p>}
          </div>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs text-white/40">© {year} {name} · {brand ? `Powered by ${brand}` : "Powered by MILTROO"}</div>
    </footer>
  );
}

/* Renders a complete non-ecommerce website from theme.site demo content. */
/* Category-adapted default Services + Pricing — rendered via the shared editor canvas so every theme keeps its own styling. */
function DefaultSections({ defaults, live, slug, theme, store }) {
  const secs = [defaults?.services, defaults?.pricing].filter(Boolean);
  if (!secs.length) return null;
  return (
    <div data-testid="default-service-pricing">
      <EditorCanvas doc={{ sections: secs }} theme={theme} store={store} live={live} slug={slug} />
    </div>
  );
}

export default function SiteRenderer({ site, head, radius = "10px", info = {}, theme = {}, store = {}, brand = "", defaults = null, live = false, slug = "" }) {
  if (!site) return null;
  const contact = { ...site.contact, ...Object.fromEntries(Object.entries(info || {}).filter(([, v]) => v)) };
  const ItemIcon = TYPE_ICONS[site.items?.type] || Zap;
  const dur = MOTION_DUR[theme?.motion] || 0.6;
  const cardStyle = theme?.card_style || "shadow";
  const cardCls = {
    shadow: "bg-white border border-black/5 hover:-translate-y-1.5 hover:shadow-xl transition-all duration-300",
    bordered: "bg-white border-2 hover:-translate-y-1.5 hover:shadow-lg transition-all duration-300",
    glass: "bg-white/70 backdrop-blur-md border border-white/60 shadow-lg hover:-translate-y-1.5 hover:shadow-xl transition-all duration-300",
    flat: "hover:-translate-y-1 transition-all duration-300",
  }[cardStyle] || "bg-white border border-black/5 hover:-translate-y-1.5 hover:shadow-xl transition-all duration-300";
  const cardExtra = {
    ...(cardStyle === "bordered" ? { borderColor: "color-mix(in srgb, var(--sp) 18%, transparent)" } : {}),
    ...(cardStyle === "flat" ? { backgroundColor: "var(--ss)" } : {}),
    borderRadius: radius,
  };
  const cards = site.items?.cards || [];

  const sections = {
    about: site.about && (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 grid lg:grid-cols-2 gap-12 items-center">
        <R dur={dur}>
          <div className="relative">
            <div className="absolute -inset-3 rounded-3xl opacity-20 blur-2xl" style={{ backgroundImage: "linear-gradient(135deg, var(--sp), var(--sa))" }} />
            <SafeImg src={site.about.image} alt="About" className="relative w-full aspect-[4/3] object-cover shadow-xl" style={{ borderRadius: radius }} />
          </div>
        </R>
        <R dur={dur} delay={0.1}>
          <p className="uppercase text-[11px] tracking-[0.25em] font-extrabold" style={{ color: "var(--sa)" }}>{site.about.overline}</p>
          <h2 className={`${head} text-4xl sm:text-5xl tracking-tighter mt-3`} style={{ color: "var(--sp)" }}>{site.about.title}</h2>
          <p className="mt-4 text-base leading-relaxed" style={{ color: "var(--st)", opacity: 0.72 }}>{site.about.body}</p>
          <div className="flex flex-wrap gap-3 mt-6">
            {["Trusted by thousands", "Award-winning team", "Premium support"].map((t) => (
              <span key={t} className="flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-full" style={{ backgroundColor: "var(--ss)", color: "var(--sp)" }}>
                <BadgeCheck className="h-3.5 w-3.5" style={{ color: "var(--sa)" }} />{t}
              </span>
            ))}
          </div>
        </R>
      </section>
    ),

    items: cards.length > 0 && (
      <section className="py-16" style={{ backgroundColor: "var(--ss)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <R dur={dur}><SectionHead center overline={site.items.overline} title={site.items.title} /></R>
          {site.items.type === "menu" ? (
            <div className="grid sm:grid-cols-2 gap-4 mt-10 max-w-4xl mx-auto">
              {cards.map((c, i) => (
                <R key={c.name} delay={i * 0.05} dur={dur}>
                  <div className={`flex gap-4 items-center p-4 ${cardCls}`} style={cardExtra} data-testid={`menu-item-${i}`}>
                    <SafeImg src={c.image} alt={c.name} className="h-16 w-16 object-cover shrink-0" style={{ borderRadius: `calc(${radius} * 0.7)` }} />
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm" style={{ color: "var(--sp)" }}>{c.name}</p>
                      <p className="text-xs mt-0.5" style={{ color: "var(--st)", opacity: 0.6 }}>{c.desc}</p>
                    </div>
                  </div>
                </R>
              ))}
            </div>
          ) : site.items.type === "posts" ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
              {cards.map((c, i) => (
                <R key={c.name} delay={i * 0.06} dur={dur}>
                  <div className={`overflow-hidden group cursor-pointer ${cardCls}`} style={cardExtra} data-testid={`post-${i}`}>
                    <div className="aspect-[16/10] overflow-hidden"><SafeImg src={c.image} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /></div>
                    <div className="p-5">
                      <span className="text-[10px] font-extrabold uppercase tracking-widest px-2.5 py-1 rounded-full" style={{ backgroundColor: "var(--ss)", color: "var(--sa)" }}>{c.tag || "Story"}</span>
                      <p className="font-display font-bold text-lg mt-3 leading-snug group-hover:underline underline-offset-4" style={{ color: "var(--sp)" }}>{c.name}</p>
                      <p className="text-sm mt-2" style={{ color: "var(--st)", opacity: 0.65 }}>{c.desc}</p>
                    </div>
                  </div>
                </R>
              ))}
            </div>
          ) : site.items.type === "courses" ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
              {cards.map((c, i) => (
                <R key={c.name} delay={i * 0.06} dur={dur}>
                  <div className={`overflow-hidden ${cardCls}`} style={cardExtra} data-testid={`course-${i}`}>
                    <div className="aspect-[16/9] overflow-hidden"><SafeImg src={c.image} alt={c.name} className="w-full h-full object-cover" /></div>
                    <div className="p-5">
                      <p className="font-display font-bold leading-snug" style={{ color: "var(--sp)" }}>{c.name}</p>
                      <p className="text-xs mt-1.5" style={{ color: "var(--st)", opacity: 0.6 }}>by {c.instructor || "Expert Instructor"}</p>
                      <p className="text-xs font-bold mt-2" style={{ color: "var(--sa)" }}>{c.desc}</p>
                      <button className="mt-4 w-full py-2.5 rounded-full text-xs font-extrabold text-white active:scale-95 transition-transform" style={{ backgroundColor: "var(--sp)" }}>Enroll Now</button>
                    </div>
                  </div>
                </R>
              ))}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
              {cards.map((c, i) => (
                <R key={c.name} delay={i * 0.06} dur={dur}>
                  <div className={`p-6 h-full group ${cardCls}`} style={cardExtra} data-testid={`service-${i}`}>
                    <div className="h-11 w-11 rounded-xl flex items-center justify-center text-white group-hover:scale-110 group-hover:rotate-3 transition-transform" style={{ backgroundImage: "linear-gradient(135deg, var(--sp), var(--sa))" }}>
                      <ItemIcon className="h-5 w-5" />
                    </div>
                    <p className="font-display font-bold text-lg mt-4" style={{ color: "var(--sp)" }}>{c.name}</p>
                    <p className="text-sm mt-1.5 leading-relaxed" style={{ color: "var(--st)", opacity: 0.68 }}>{c.desc}</p>
                  </div>
                </R>
              ))}
            </div>
          )}
        </div>
      </section>
    ),

    stats: site.stats?.length > 0 && (
      <section className="py-14" style={{ backgroundImage: "linear-gradient(135deg, var(--sp), var(--sa))" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-2 lg:grid-cols-4 gap-8 text-center text-white">
          {site.stats.map((s, i) => (
            <R key={s.label} delay={i * 0.08} dur={dur}>
              <p className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter" data-testid={`stat-${i}`}><Counter value={s.value} dur={dur * 2000} /></p>
              <p className="text-xs font-bold uppercase tracking-widest mt-2 text-white/75">{s.label}</p>
            </R>
          ))}
        </div>
      </section>
    ),

    team: site.team?.length > 0 && (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <R dur={dur}><SectionHead center overline="The People" title="Meet the Team" /></R>
        <div className="flex flex-wrap justify-center gap-5 mt-10">
          {site.team.map((m, i) => (
            <R key={m.name} delay={i * 0.06} dur={dur}>
              <div className={`px-8 py-6 text-center ${cardCls}`} style={cardExtra} data-testid={`team-${i}`}>
                <div className="h-14 w-14 mx-auto rounded-full flex items-center justify-center text-white font-display font-extrabold text-lg" style={{ backgroundImage: "linear-gradient(135deg, var(--sp), var(--sa))" }}>
                  {m.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                </div>
                <p className="font-bold text-sm mt-3" style={{ color: "var(--sp)" }}>{m.name}</p>
                <p className="text-xs" style={{ color: "var(--st)", opacity: 0.55 }}>{m.role}</p>
              </div>
            </R>
          ))}
        </div>
      </section>
    ),

    testimonials: site.testimonials?.length > 0 && (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <R dur={dur}><SectionHead center overline="Testimonials" title="What People Say" /></R>
        <div className="grid sm:grid-cols-3 gap-5 mt-10">
          {site.testimonials.map((t, i) => (
            <R key={t.name} delay={i * 0.08} dur={dur}>
              <div className={`p-6 h-full ${cardCls}`} style={cardExtra}>
                <Quote className="h-5 w-5 mb-3" style={{ color: "var(--sa)" }} />
                <Stars rating={5} />
                <p className="text-sm leading-relaxed mt-3" style={{ color: "var(--st)", opacity: 0.78 }}>"{t.text}"</p>
                <p className="text-xs font-extrabold mt-4" style={{ color: "var(--sp)" }}>{t.name}</p>
              </div>
            </R>
          ))}
        </div>
      </section>
    ),

    faq: site.faq?.length > 0 && (
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
        <R dur={dur}><SectionHead center overline="FAQ" title="Common Questions" /></R>
        <div className="mt-8"><Faq items={site.faq} cardCls={cardCls} cardExtra={cardExtra} /></div>
      </section>
    ),

    cta: site.cta && (
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16">
        <R dur={dur}>
          <div className="rounded-3xl p-10 sm:p-14 text-center text-white relative overflow-hidden" style={{ backgroundImage: "linear-gradient(135deg, var(--sp), var(--sa))" }} data-testid="site-cta">
            <div className="absolute -top-14 -right-14 h-48 w-48 rounded-full bg-white/10 animate-float" />
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tighter relative">{site.cta.title}</h2>
            <a href={`tel:${contact.phone}`} className="inline-flex items-center gap-2 mt-7 px-9 py-4 rounded-full bg-white font-bold text-sm active:scale-95 transition-transform relative" style={{ color: "var(--sp)" }}>
              {site.cta.button} <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </R>
      </section>
    ),

    contact: (
      <section id="site-contact" className="max-w-6xl mx-auto px-4 sm:px-6 pb-20">
        <R dur={dur}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="site-contact-cards">
            {[
              [Phone, "Call us", contact.phone], [Mail, "Email", contact.email],
              [MapPin, "Visit", contact.address], ...(contact.hours ? [[Clock, "Hours", contact.hours]] : []),
            ].map(([Icon, label, val]) => (
              <div key={label} className={`p-5 flex items-start gap-3 ${cardCls}`} style={cardExtra}>
                <div className="h-10 w-10 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: "var(--ss)", color: "var(--sa)" }}><Icon className="h-4 w-4" /></div>
                <div className="min-w-0"><p className="text-[10px] uppercase tracking-widest font-extrabold" style={{ color: "var(--st)", opacity: 0.45 }}>{label}</p>
                  <p className="text-sm font-bold mt-0.5 break-words" style={{ color: "var(--sp)" }}>{val}</p></div>
              </div>
            ))}
          </div>
        </R>
      </section>
    ),
  };

  const order = Array.isArray(site.section_order) && site.section_order.length ? site.section_order : Object.keys(sections);

  return (
    <div data-testid="site-renderer" id="site-content">
      {order.map((k) => (
        <div key={k}>
          {sections[k] || null}
          {k === "items" && <DefaultSections defaults={defaults} live={live} slug={slug} theme={theme} store={store} />}
        </div>
      ))}
      {!order.includes("items") && <DefaultSections defaults={defaults} live={live} slug={slug} theme={theme} store={store} />}
      <SiteFooter theme={theme} site={site} store={store} head={head} contact={contact} brand={brand} />
    </div>
  );
}
